import { useEffect, useMemo } from 'react';
import { ZfBox, ZfIcon, ZfStack, ZfTextareaField, ZfToggle, ZfTooltip, ZfTypography } from '@zafire/react';
import { useFormikContext } from 'formik';
import {
  PortfolioCreationFlow,
  PortfolioTypeRule,
  SequenceNumberForBrStatusEnum,
} from 'pco-product-service-internal-consumer';
import { useTranslation } from 'react-i18next';
import { PortfolioForm } from '../AddPortfolioDialog';
import { FormSelectField, SelectFieldOption, toLookupOptions } from '../common/FormSelectField';
import { FormTextField } from '../common/FormTextField';
import useGetBeneficialOwners from '../hooks/useGetBeneficialOwners';
import { useGetDropdown } from '../hooks/useGetDropdown';
import useGetSequenceNumber from '../hooks/useGetSequenceNumber';
import { EXTERNAL_ACCOUNT_NUMBER_MAX_LENGTH, REMARKS_MAX_LENGTH } from '../portfolioTypeDependentFields';
import {
  getFieldValues,
  portfolioTypesForFlow,
  ResolvedFieldRules,
  resolveFieldRules,
} from '../portfolioTypeFieldRules';

type PortfolioConfigSectionProps = {
  brUid: string;
  isLoading: boolean;
  isStandaloneCreate: boolean;
  isEdit: boolean;
  existingPortfolio?: { canEditDetails?: boolean };
  portfolioRules?: readonly PortfolioTypeRule[];
};

const toSelectOptions = <Item,>(
  items: readonly Item[],
  toOption: (item: Item) => { value?: string; label?: string; disabled?: boolean },
): SelectFieldOption[] =>
  items.flatMap((item) => {
    const { value, label, disabled } = toOption(item);
    return value === undefined ? [] : [{ value, label: label ?? value, disabled }];
  });

export const PortfolioConfigSection = ({
  brUid,
  isLoading,
  isStandaloneCreate,
  isEdit,
  existingPortfolio,
  portfolioRules,
}: PortfolioConfigSectionProps) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<PortfolioForm>();
  const fieldRules = resolveFieldRules({
    rules: portfolioRules ?? [],
    portfolioType: String(values.portfolioType),
    isStandaloneCreate,
    isEdit,
    existingPortfolio,
  });
  const { currencyData, portfolioTypeData, externalAccountIdentifierTypeData } = useGetDropdown();
  const externalAccountTypeOptions = useMemo(
    () => toLookupOptions(externalAccountIdentifierTypeData ?? []),
    [externalAccountIdentifierTypeData],
  );

  const { data: sequenceNumberList, isLoading: sequenceNumberLoading } = useGetSequenceNumber(
    brUid,
    values.portfolioType,
  );

  const sequenceList = useMemo(
    () =>
      sequenceNumberList?.sequenceNumbers?.filter((item) => item.status !== SequenceNumberForBrStatusEnum.Excluded) ??
      [],
    [sequenceNumberList],
  );

  useEffect(() => {
    if (values.sequenceNumber === '' && sequenceList.length > 0) {
      const firstActiveSequence = sequenceList.find((item) => item.status === SequenceNumberForBrStatusEnum.Active);
      setFieldValue('sequenceNumber', firstActiveSequence?.sequenceNumber || '');
    }
  }, [sequenceList, setFieldValue, values.sequenceNumber]);

  useEffect(() => {
    if (portfolioTypeData && values.portfolioTypeNumber === '') {
      const item = portfolioTypeData?.find((item) => item.key === values.portfolioType);
      if (item) {
        setFieldValue('portfolioTypeNumber', item.value.code);
      }
    }
  }, [portfolioTypeData, setFieldValue, values.portfolioType, values.portfolioTypeNumber]);

  const { data: beneficialData } = useGetBeneficialOwners(brUid || '', brUid !== '');

  const beneficialDataList = useMemo(() => {
    if (beneficialData) {
      return beneficialData.filter((item) => item.valid);
    }
    return [];
  }, [beneficialData]);

  const currencyOptions = useMemo(() => toLookupOptions(currencyData ?? []), [currencyData]);

  const portfolioTypeOptions = useMemo(() => {
    const creationFlow = isStandaloneCreate ? PortfolioCreationFlow.Standalone : PortfolioCreationFlow.ServiceAgreement;
    const allowedTypes = !isEdit && portfolioRules ? portfolioTypesForFlow(portfolioRules, creationFlow) : undefined;
    return toLookupOptions(
      (portfolioTypeData ?? []).filter((item) => !allowedTypes || allowedTypes.some((type) => type === item.key)),
    );
  }, [portfolioTypeData, portfolioRules, isStandaloneCreate, isEdit]);

  const sequenceNumberOptions = useMemo(
    () =>
      toSelectOptions(sequenceList, (item) => ({
        value: item.sequenceNumber,
        disabled: item.status === SequenceNumberForBrStatusEnum.Inactive,
      })),
    [sequenceList],
  );

  const beneficialOwnerOptions = useMemo(
    () =>
      toSelectOptions(beneficialDataList, (item) => ({
        value: item.partnerUid,
        label: `${item.partnerName} (${item.partnerId}) - ${item.partnerToBrRole}`,
      })),
    [beneficialDataList],
  );

  const handlePortfolioTypeChange = (portfolioType: string) => {
    const portfolioTypeNumber = portfolioTypeData?.find((item) => item.key === portfolioType)?.value.code;
    setFieldValue('portfolioTypeNumber', portfolioTypeNumber);
    if (isStandaloneCreate) {
      const valuesForPortfolioType = getFieldValues({
        rules: portfolioRules ?? [],
        portfolioType,
        isStandaloneCreate,
        isEdit: false,
      });
      Object.entries(valuesForPortfolioType).forEach(([field, value]) => setFieldValue(field, value));
    }
  };

  const isVisible = (field: keyof ResolvedFieldRules) => fieldRules[field].state !== 'hidden';

  const stateProps = (field: keyof ResolvedFieldRules) => ({
    mandatory: fieldRules[field].required === true,
    readonly: fieldRules[field].state === 'disabled',
  });

  const isExternalAccountTypeVisible = isVisible('externalAccountType');

  useEffect(() => {
    if (isExternalAccountTypeVisible && !values.externalAccountType && externalAccountTypeOptions.length > 0) {
      setFieldValue('externalAccountType', externalAccountTypeOptions[0].value);
    }
  }, [isExternalAccountTypeVisible, values.externalAccountType, externalAccountTypeOptions, setFieldValue]);

  const typeDependentFieldCount = [
    isVisible('details'),
    isVisible('externalAccountNumber'),
    isVisible('externalAccountType'),
  ].filter(Boolean).length;

  return (
    <ZfStack spacing="medium">
      <ZfStack direction="row" spacing="small" styles={{ alignItems: 'end' }}>
        <FormSelectField
          name="portfolioType"
          label={t('pb.portfoliodetail.portfoliotype.label')}
          mandatory
          options={portfolioTypeOptions}
          isLoading={isLoading}
          readonly={!isStandaloneCreate}
          onChange={handlePortfolioTypeChange}
        />
        {isVisible('referenceCurrency') && (
          <FormSelectField
            name="referenceCurrency"
            label={t('pb.portfoliodetail.referencecurrency.label')}
            tooltip={t('pb.portfoliodetail.referencecurrency.info')}
            options={currencyOptions}
            isLoading={isLoading}
            {...stateProps('referenceCurrency')}
          />
        )}
        {isVisible('sequenceNumber') && (
          <FormSelectField
            name="sequenceNumber"
            label={t('pb.portfoliodetail.sequencenumber.label')}
            placeholder={t('pb.portfoliodetail.sequencenumber.placeholder')}
            options={sequenceNumberOptions}
            isLoading={sequenceNumberLoading || isLoading}
            {...stateProps('sequenceNumber')}
          />
        )}
      </ZfStack>
      {(isVisible('chargingCurrency') || isVisible('rubrik') || isVisible('specialBoEnabled')) && (
        <ZfStack direction="row" spacing="small" styles={{ alignItems: 'end' }}>
          {isVisible('chargingCurrency') && (
            <FormSelectField
              name="chargingCurrency"
              label={t('pb.portfoliodetail.chargingcurrency.label')}
              options={currencyOptions}
              isLoading={isLoading}
              {...stateProps('chargingCurrency')}
            />
          )}
          {isVisible('rubrik') && (
            <FormTextField
              name="rubrik"
              label={t('pb.portfoliodetail.rubric.label')}
              maxLength={200}
              placeholder={t('pb.portfoliodetail.rubric.placeholder')}
              isLoading={isLoading}
              {...stateProps('rubrik')}
            />
          )}
          {isVisible('specialBoEnabled') && (
            <ZfBox style={{ flex: 1, minWidth: 0 }}>
              <ZfToggle
                style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                label={t('pb.portfoliodetail.allocatedbo.label')}
                checked={values.specialBoEnabled}
                disabled={fieldRules.specialBoEnabled.state === 'disabled'}
                onToggleChange={() => {
                  if (values.specialBoEnabled) {
                    setFieldValue('allocatedBo', '');
                  }
                  setFieldValue('specialBoEnabled', !values.specialBoEnabled);
                }}
              >
                <ZfBox slot="trailing">
                  <ZfIcon name="info" id="playground-anchor" aria-describedby="playground-tooltip" size="large" />
                  <ZfTooltip
                    id="playground-tooltip"
                    anchor="playground-anchor"
                    side="left"
                    alignment="start"
                    content-alignment=""
                  >
                    <ZfBox styles={{ whiteSpace: 'pre-wrap' }}>
                      <ZfTypography variant="body-small">{t('pb.portfoliodetail.allocatedbo.tooltip')}</ZfTypography>
                    </ZfBox>
                  </ZfTooltip>
                </ZfBox>
              </ZfToggle>
            </ZfBox>
          )}
        </ZfStack>
      )}
      {values.specialBoEnabled && isVisible('allocatedBo') && (
        <ZfStack direction="row" spacing="small" styles={{ alignItems: 'end' }}>
          <FormSelectField
            name="allocatedBo"
            label={t('pb.portfoliodetail.allocatedbo.label')}
            options={beneficialOwnerOptions}
            isLoading={isLoading}
            {...stateProps('allocatedBo')}
          />
        </ZfStack>
      )}
      {typeDependentFieldCount > 0 && (
        <ZfStack direction="row" spacing="small" styles={{ alignItems: 'start' }}>
          {isVisible('details') && (
            <FormTextField
              name="details"
              label={t('pb.portfoliodetail.details.field.label')}
              maxLength={1000}
              mandatory={fieldRules.details.required === true}
              disabled={fieldRules.details.state === 'disabled'}
            />
          )}
          {isVisible('externalAccountNumber') && (
            <FormTextField
              name="externalAccountNumber"
              label={t('pb.portfoliodetail.externalaccountnumber.label')}
              maxLength={EXTERNAL_ACCOUNT_NUMBER_MAX_LENGTH}
              {...stateProps('externalAccountNumber')}
            />
          )}
          {isVisible('externalAccountType') && (
            <FormSelectField
              name="externalAccountType"
              label={t('pb.portfoliodetail.externalaccounttype.label')}
              options={externalAccountTypeOptions}
              allowSearch={false}
              {...stateProps('externalAccountType')}
            />
          )}
          {typeDependentFieldCount < 3 && <div style={{ flex: 3 - typeDependentFieldCount }} />}
        </ZfStack>
      )}
      {isVisible('remarks') && (
        <ZfStack direction="row" spacing="small">
          <ZfTextareaField
            style={{ flex: 1 }}
            id="remarks"
            name="remarks"
            label={t('pb.portfoliodetail.remarks.label')}
            placeholder={t('pb.portfoliodetail.remarks.placeholder')}
            maxlength={REMARKS_MAX_LENGTH}
            value={values.remarks}
            state={fieldRules.remarks.state === 'disabled' ? 'readonly' : 'default'}
            onValueChange={(event) => setFieldValue('remarks', event.detail)}
          />
        </ZfStack>
      )}
    </ZfStack>
  );
};
